import React, { useEffect, useState } from 'react';

import { LayoutGroup } from 'framer-motion';

import { randomBoolean } from '@pkg/utils';

import { Leaving, Present } from './components';
import { tokens } from './tokens';
import { type LeavingItems, type PresentItemProps, type StackItems } from './types';

import { LeavingContainer, List, PresentContainer, StyledThumbnailStack } from './styles';

export interface ThumbnailStackProps {
    items?: StackItems;
    animateExit?: boolean;
}

const HASH_JOINER = '-';

function trimItems(items: StackItems) {
    return items.slice(0, tokens.list.maxItems);
}

export function ThumbnailStack({ items = [], animateExit = false }: ThumbnailStackProps) {
    const [presentItems, setPresentItems] = useState<StackItems>(trimItems(items));
    const [leavingItems, setLeavingItems] = useState<LeavingItems>([]);

    function handleRemoveLeavingItem(id: PresentItemProps['id']) {
        setLeavingItems((leaving) => leaving.filter((leavingItem) => leavingItem.id !== id));
    }

    useEffect(() => {
        // An early return means we use the `items` prop
        // (instead of `presentItems`) to generate our markup.
        // Toggling `animateExit` during the component's life
        // means we get a "burst of leaving items". This isn't
        // a concern, since this prop isn't likely to get toggled.
        if (!animateExit) return;

        const trimmedNewItems = trimItems(items);

        const newIds = trimmedNewItems.map(({ id }) => id);

        const newIdHash = newIds.join(HASH_JOINER);
        const previousIdHash = presentItems.map(({ id }) => id).join(HASH_JOINER);

        if (newIdHash === previousIdHash) return;

        setPresentItems(trimmedNewItems);
        const itemsToRemove = presentItems.filter(({ id }) => !newIds.includes(id));

        if (itemsToRemove.length) {
            setLeavingItems((leaving) => {
                const leavingIds = leaving.map(({ id }) => id);
                const uniqueItemsToRemove = itemsToRemove
                    .filter((remove) => !leavingIds.includes(remove.id))
                    .map((remove) => ({ ...remove, forward: randomBoolean() }));

                return [...leaving, ...uniqueItemsToRemove];
            });
        }

        // Intentionally keeping `presentItems` out of deps.
        // It changes (at most) whenever `items` changes, so we
        // already have that render-cycle covered, and we don't
        // want a 2nd re-render after we update `presentItems`.

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, animateExit]);

    const stackItems = animateExit ? presentItems : trimItems(items);

    // Intentionally not setting `autoPlay` on `video` items.
    // We will wait to see how overall performance is before enabling.
    const presentItemsMarkup = stackItems.length ? (
        stackItems.map(({ id, videoUrl, imageUrl, title }, i) => (
            <Present
                key={`ThumbnailStack-Present-${id}-${i}`}
                id={id}
                videoUrl={videoUrl}
                imageUrl={imageUrl}
                title={title}
            />
        ))
    ) : (
        // Consider `altText` to describe "There are no items in this list".
        <Present key="ThumbnailStack-Fallback" id="ThumbnailStack-Fallback" title="" />
    );

    const leavingItemsMarkup =
        animateExit && leavingItems.length
            ? leavingItems.map(({ id, videoUrl, imageUrl, title, forward }) => (
                  <Leaving
                      key={`ThumbnailStack-Leaving-${id}`}
                      id={id}
                      videoUrl={videoUrl}
                      imageUrl={imageUrl}
                      title={title}
                      forward={forward}
                      onAnimationComplete={() => handleRemoveLeavingItem(id)}
                  />
              ))
            : null;

    const leavingContainerMarkup = animateExit ? (
        <LeavingContainer>
            <List>{leavingItemsMarkup}</List>
        </LeavingContainer>
    ) : null;

    return (
        <StyledThumbnailStack>
            <PresentContainer>
                <List>
                    <LayoutGroup>{presentItemsMarkup}</LayoutGroup>
                </List>
            </PresentContainer>

            {leavingContainerMarkup}
        </StyledThumbnailStack>
    );
}
